/**
 * Map utility functions: color maps, popup HTML, route drawing.
 */

export const STATUS_COLORS = {
  pending: '#9CA3AF',
  in_progress: '#F59E0B',
  delivered: '#10B981',
  problem: '#EF4444',
};

export const STATUS_LABELS = {
  pending: 'En attente',
  in_progress: 'En cours',
  delivered: 'Livré',
  problem: 'Problème',
};

/** Palette of distinct colors for drivers */
export const DRIVER_PALETTE = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
];

/**
 * Build a color map from assigned driver IDs.
 * @param {object[]} clientsList
 * @returns {Record<string, string>}
 */
export function buildDriverColorMap(clientsList) {
  const ids = [...new Set(clientsList.map((c) => c.assigned_driver).filter(Boolean))];
  const map = {};
  ids.forEach((id, i) => { map[id] = DRIVER_PALETTE[i % DRIVER_PALETTE.length]; });
  return map;
}

/**
 * Build a lookup of client_id to stop info from detailed plans.
 * @param {object[]} plans
 * @returns {Record<string, object>}
 */
export function buildStopMap(plans) {
  const map = {};
  for (const plan of plans) {
    for (const stop of plan.stops || []) {
      map[stop.client_id] = stop;
    }
  }
  return map;
}

/**
 * Build popup HTML with Google Maps and Waze navigation links.
 * @param {object} client
 * @param {string} estimatedTime
 * @param {object} [stopInfo] - Stop info from plan (contains plan_id)
 * @returns {string}
 */
export function buildPopupHtml(client, estimatedTime, stopInfo) {
  const statusLabel = STATUS_LABELS[client.delivery_status] || '';
  const statusColor = STATUS_COLORS[client.delivery_status] || '#9CA3AF';
  const time = estimatedTime ? `<span style="color:#64748b;font-size:11px;">~${estimatedTime}</span>` : '';

  // Header: name + status badge
  let html = `<div style="min-width:220px;font-family:system-ui,sans-serif;">`;
  html += `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">`;
  html += `<b style="font-size:13px;">${client.name}</b>`;
  html += `<span style="background:${statusColor};color:#fff;font-size:10px;padding:2px 8px;border-radius:10px;">${statusLabel}</span>`;
  html += `</div>`;

  // Address
  if (client.address) {
    html += `<div style="font-size:11px;color:#64748b;margin-bottom:4px;">${client.address}${client.city ? `, ${client.city}` : ''}</div>`;
  }

  // Units + time
  html += `<div style="font-size:12px;margin-bottom:6px;"><b>${client.nb_units || 0} unités</b> ${time}</div>`;

  // Contact: phone + email
  if (client.phone) {
    html += `<a href="tel:${client.phone}" style="display:flex;align-items:center;gap:4px;font-size:11px;color:#1e40af;text-decoration:none;margin-bottom:3px;">📞 ${client.phone}</a>`;
  }
  if (client.email) {
    html += `<a href="mailto:${client.email}" style="font-size:11px;color:#1e40af;text-decoration:none;margin-bottom:3px;">✉ ${client.email}</a>`;
  }

  // Separator
  html += `<hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0;">`;

  // Action buttons row
  html += `<div style="display:flex;gap:4px;flex-wrap:wrap;">`;

  // Deliver button (if client has an active plan stop)
  if (stopInfo?.plan_id && client.delivery_status !== 'delivered') {
    html += `<a href="/delivery/${stopInfo.plan_id}?stop=${stopInfo.id}" style="font-size:11px;background:#059669;color:#fff;padding:5px 12px;border-radius:8px;text-decoration:none;font-weight:600;">▶ Livrer</a>`;
  }

  // Fiche client
  html += `<a href="/clients/${client.id}" style="font-size:11px;background:#1e40af;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Fiche</a>`;

  // Navigation buttons
  if (client.lat && client.lng) {
    const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${client.lat},${client.lng}`;
    const waze = `https://waze.com/ul?ll=${client.lat},${client.lng}&navigate=yes`;
    html += `<a href="${gmaps}" target="_blank" rel="noopener" style="font-size:11px;background:#059669;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Maps</a>`;
    html += `<a href="${waze}" target="_blank" rel="noopener" style="font-size:11px;background:#0891b2;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Waze</a>`;
  }

  // Call button
  if (client.phone) {
    html += `<a href="tel:${client.phone}" style="font-size:11px;background:#f97316;color:#fff;padding:4px 10px;border-radius:8px;text-decoration:none;font-weight:500;">Appeler</a>`;
  }

  html += `</div></div>`;
  return html;
}

/**
 * Draw route polylines on the map for given plans.
 * Uses OSRM public API for real road routing, falls back to straight lines.
 * @param {object[]} plans
 * @param {Record<string, string>} colorMap
 * @param {object} map - Leaflet map instance
 * @param {{ current: object[] }} layersRefObj
 */
export function drawRoutes(plans, colorMap, map, layersRefObj) {
  const L = window.L;
  for (const plan of plans) {
    const stops = (plan.stops || []).filter((s) => s.lat && s.lng);
    if (stops.length < 2) continue;

    const color = colorMap[plan.driver_id] || '#3B82F6';
    fetchOsrmRoute(stops, color, map, layersRefObj, L);
  }
}

/**
 * Fetch real road route from OSRM and draw it on the map.
 * Falls back to straight polyline if OSRM fails.
 */
async function fetchOsrmRoute(stops, color, map, layersRefObj, L) {
  const coords = stops.map((s) => `${s.lng},${s.lat}`).join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('OSRM error');
    const data = await res.json();

    if (data.routes && data.routes[0]) {
      const route = data.routes[0];
      const geojsonCoords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);

      const polyline = L.polyline(geojsonCoords, {
        color, weight: 4, opacity: 0.8,
      });
      polyline.addTo(map);
      layersRefObj.current.push(polyline);

      // Add distance/duration popup on route click
      const distKm = (route.distance / 1000).toFixed(1);
      const durMin = Math.round(route.duration / 60);
      polyline.bindPopup(
        `<div style="font-family:system-ui,sans-serif;font-size:12px;">` +
        `<b>Itinéraire</b><br/>` +
        `📏 ${distKm} km — ⏱ ${durMin} min<br/>` +
        `<span style="color:#64748b;">${stops.length} arrêts</span></div>`
      );
    } else {
      drawStraightLine(stops, color, map, layersRefObj, L);
    }
  } catch (_err) {
    drawStraightLine(stops, color, map, layersRefObj, L);
  }
}

/** Fallback: straight dashed line between stops */
function drawStraightLine(stops, color, map, layersRefObj, L) {
  const latlngs = stops.map((s) => [s.lat, s.lng]);
  const polyline = L.polyline(latlngs, {
    color, weight: 3, opacity: 0.7, dashArray: '8, 6',
  });
  polyline.addTo(map);
  layersRefObj.current.push(polyline);
}
